import axios from "../util/axios";
import { createSelector } from "reselect";
import type { Dispatch } from "redux";

interface SimplifiedPartFinderUser {
  login: string;
  email: string;
}

interface SimplifiedPartFinderPermissions {
  "access-documents": true;
  admin: true;
  "admin-division": true;
  "admin-emails": true;
  "admin-show-access": true;
  "admin-tasks": true;
  "admin-users": true;
  "show-cart": true;
  "show-catalog": true;
  "show-ci-mode": true;
  "show-price": true;
  "show-print": true;
  "show-refurbished": true;
  "show-internal-parts": true;
  "show-all-prices": true;
  "show-traffic-light": true;
  "show-all-substs": true;
}

interface PartFinderSessionData {
  user: SimplifiedPartFinderUser;
  /**
   * - TODO:  number means?
   * - null means no auto logout
   */
  autoLogoutDelay: number | null;
  cacheBuster: string;
  permissions: Partial<SimplifiedPartFinderPermissions>;
  currencies: string[];
  currency: string | null;
  loginUrl: string;
}

const SET_SESSION_DATA = "easykat/auth/SET_SESSION";
const SET_LOGIN_ERROR = "easykat/auth/SET_LOGIN_ERROR";
const CLEAR_LOGIN_ERROR = "easykat/auth/CLEAR_LOGIN_ERROR";
const SET_SESSION_ERROR = "easykat/auth/SET_SESSION_ERROR";
const SET_LOGOUT_TIME = "easykat/auth/SET_SESSION_LOGOUT_TIME";

const setSessionData = (data: PartFinderSessionData) => ({ type: SET_SESSION_DATA, payload: data } as const);
const setSessionError = (data: Error) => ({ type: SET_SESSION_ERROR, error: true, payload: data } as const);
const setLoginError = (data: Error) => ({ type: SET_LOGIN_ERROR, error: true, payload: data } as const);

export const setLogoutTime = (time: number) => ({ type: SET_LOGOUT_TIME, payload: time } as const);

export const clearLoginError = () => ({ type: CLEAR_LOGIN_ERROR } as const);

type SessionAtomicAction =
  | ReturnType<typeof setSessionData>
  | ReturnType<typeof setSessionError>
  | ReturnType<typeof setLoginError>
  | ReturnType<typeof setLogoutTime>
  | ReturnType<typeof clearLoginError>;

export function fetchSession() {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      let response = await axios.get("auth/session?_=" + Date.now());
      dispatch(setSessionData(response.data));
    } catch (err) {
      setSessionError(err);
    }
  };
}

export function updateSession(data: unknown) {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      let response = await axios.put("auth/session", data);
      dispatch(setSessionData(response.data));
    } catch (err) {
      setSessionError(err);
    }
  };
}

export function login(data: unknown) {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await axios.post("auth/login", data);
      dispatch(setSessionData(response.data));
    } catch (err) {
      console.error(err);
      dispatch(setLoginError(err));
    }
  };
}

export function logout(data: unknown) {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await axios.post("auth/logout", data);
      dispatch(setSessionData(response.data));
    } catch (err) {
      console.error(err);
    }
  };
}

export function updateUserSettings(data: unknown) {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await axios.post("auth/session", data);
      dispatch(setSessionData(response.data));
    } catch (err) {
      console.error(err);
    }
  };
}

export function sessionSelector(state: { session: SessionReducerState }): SessionReducerState {
  return state.session;
}

export const getIsLoggedIn = createSelector(sessionSelector, (session) => !!session.user);

export const getPermissions = createSelector(sessionSelector, (session) => session.permissions);

export const getShowCartPermission = createSelector(getPermissions, (permissions) => permissions["show-cart"]);

/**
 * @return {string[]}
 */
export const getCurrencies = createSelector(sessionSelector, (session) => session.currencies ?? []);

export const getUser = createSelector(sessionSelector, (session) => session.user);

export const getLoginUrl = createSelector(sessionSelector, (session) => session.loginUrl);

// export const getLoginError = createSelector(
//   sessionSelector,
//   session => session.loginError
// );

export const getCacheBuster = createSelector(sessionSelector, (session) => session.cacheBuster);

export const getHasLoginError = createSelector(sessionSelector, (session) => !!session.loginError);

export const getLogoutDelay = createSelector(sessionSelector, (session) => session.autoLogoutDelay);
export const getLogoutTime = createSelector(sessionSelector, (session) => session.logoutTime);
export const getLogoutTimeDisplayTreshold = createSelector(getLogoutDelay, (autoLogoutDelay) => Math.max(60e3, autoLogoutDelay - 60e3));

interface SessionReducerState extends PartFinderSessionData {
  /**
   * We cannot use Error here, since this could cause leaking of login
   * information to the bug report form
   */
  loginError: {};
  sessionError: Error;
  logoutTime: number;
}

const initialSessionState: SessionReducerState = {
  user: null,
  permissions: {},
  currencies: [],
  currency: null,
  loginError: null,
  loginUrl: "/",
  sessionError: null,
  autoLogoutDelay: null,
  logoutTime: null,
  cacheBuster: null,
};

export default function sessionReducer(state = initialSessionState, action: SessionAtomicAction): SessionReducerState {
  switch (action.type) {
    case SET_SESSION_DATA:
      return {
        ...state,
        user: action.payload.user,
        permissions: action.payload.permissions,
        loginUrl: action.payload.loginUrl,
        cacheBuster: action.payload.cacheBuster,
        autoLogoutDelay: action.payload.autoLogoutDelay,
        currencies: action.payload.currencies,
        currency: action.payload.currency,
        loginError: null,
        sessionError: null,
      };

    case SET_LOGIN_ERROR:
      return {
        ...state,
        loginError: {},
      };

    case CLEAR_LOGIN_ERROR:
      return {
        ...state,
        loginError: null,
      };

    case SET_SESSION_ERROR:
      return {
        ...state,
        sessionError: action.payload,
      };

    case SET_LOGOUT_TIME:
      return {
        ...state,
        logoutTime: action.payload,
      };

    default:
      return state;
  }
}
